import { FC } from 'react';

import Link from 'next/link';
import router from 'next/router';

import { Collapse } from '@mui/material';

import { IoMenuSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';

import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';

import { finanialAgencyLoginUserDataAtom } from '@recoil_iqfu/atoms/auth/financialAgencyLoginUserData';

import { useMenuToggle, TargetMenu } from '@hooks_common/useMenuToggle';

import styles from './SideMenu.module.scss';

type Props = {
  toggleSpMenu: () => void;
  spMenuOpen: boolean;
};

const FinancialAgencySideMenu: FC<Props> = ({ toggleSpMenu, spMenuOpen }) => {
  const { getFinancialAgencyDashboardData } = useDashboardData();
  const { data: dashboardData } = useQuery(['getFinancialAgencyDashboardData'], () =>
    getFinancialAgencyDashboardData(),
  );

  const loginUserData = useRecoilValue(finanialAgencyLoginUserDataAtom);
  const serviceList = loginUserData?.services;

  const hasLeasebackService = serviceList?.includes('leaseback');
  const hasReferSellerService = serviceList?.includes('refer_seller');
  const hasProService = serviceList?.includes('pro');

  console.log('loginUserData', loginUserData);

  const {
    iqfuMenuToggleState: { openPro, openChat },
    iqfuMenuToggleHandler,
  } = useMenuToggle();

  return (
    <div className={`${styles.container} ${spMenuOpen && styles.sp_open}`}>
      <div className={`${styles.container_inner}`}>
        <div className={styles.sp_header}>
          <div className={styles.logo_wrap}>
            {!spMenuOpen ? (
              <Link href={`/console/financial_agency/chat/contact`}>
                <a>
                  <img src="/images/iqfu/dashboard/logo.svg" alt="イクラ不動産" />
                </a>
              </Link>
            ) : (
              <div />
            )}
            {spMenuOpen && <MdClose onClick={toggleSpMenu} />}
            {!spMenuOpen && <IoMenuSharp onClick={toggleSpMenu} />}
          </div>
        </div>

        <div className={styles.menu_container}>
          {hasProService && (
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_pro}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.PRO)}
              >
                イクラ不動産Pro
                <img
                  src="/images/iqloud/side-menu-open.svg"
                  alt="開閉アイコン"
                  className={`${styles.menuToggleIcon} ${openPro && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openPro}>
                <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu}`}>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/financial_agency/iqfu_pro`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/financial_agency/iqfu_pro` ? styles.current : ''
                          }`}
                        >
                          成約事例検索
                        </div>
                      </a>
                    </Link>
                  </div>
                  <div className={styles.sideMenuItem}>
                    <Link href={`/console/financial_agency/iqfu_pro/map`}>
                      <a>
                        <div
                          className={`${styles.menuText} ${
                            router.pathname === `/console/financial_agency/iqfu_pro/map` ? styles.current : ''
                          }`}
                        >
                          成約事例Map検索
                        </div>
                      </a>
                    </Link>
                  </div>
                </div>
              </Collapse>
            </>
          )}
          <div
            className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_chat} ${
              openChat && styles.menuToggleIcon_isOpen
            }`}
            onClick={() => iqfuMenuToggleHandler(TargetMenu.CHAT)}
          >
            売却相談チャット
            <img
              src="/images/iqloud/side-menu-open.svg"
              alt="開閉アイコン"
              className={`${styles.menuToggleIcon} ${openChat && styles.menuToggleIcon_isOpen}`}
            />
          </div>
          <Collapse in={openChat}>
            <div className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_chat}`}>
              {hasLeasebackService && (
                <div className={styles.sideMenuItem}>
                  <Link href={`/console/financial_agency/chat/consulting`}>
                    <a>
                      <div
                        className={`${styles.menuText} ${
                          router.pathname === `/console/financial_agency/chat/consulting` ? styles.current : ''
                        }`}
                      >
                        不動産会社チャット
                        {dashboardData && dashboardData?.unread_consulting_room_count > 0 && (
                          <img
                            src="/images/iqfu/dashboard/unread_icon.svg"
                            alt="未読"
                            className={styles.menu_unread_icon}
                          />
                        )}
                      </div>
                    </a>
                  </Link>
                </div>
              )}
              {hasLeasebackService && (
                <div className={styles.sideMenuItem}>
                  <Link href={`/console/financial_agency/chat/seller_consulting`}>
                    <a>
                      <div
                        className={`${styles.menuText} ${styles.menuText_iqloud} ${
                          router.pathname === `/console/financial_agency/chat/seller_consulting` ? styles.current : ''
                        }`}
                      >
                        売主チャット
                        {dashboardData && dashboardData?.unread_seller_financial_agency_division_room_count > 0 && (
                          <img
                            src="/images/iqfu/dashboard/unread_icon.svg"
                            alt="未読"
                            className={styles.menu_unread_icon}
                          />
                        )}
                      </div>
                    </a>
                  </Link>
                </div>
              )}
              <div className={styles.sideMenuItem}>
                <Link href={`/console/financial_agency/chat/contact`}>
                  <a>
                    <div
                      className={`${styles.menuText} ${
                        router.pathname === `/console/financial_agency/chat/contact` ? styles.current : ''
                      }`}
                    >
                      運営問合せ
                      {dashboardData && dashboardData?.unread_inquiry_count > 0 && (
                        <img
                          src="/images/iqfu/dashboard/unread_icon.svg"
                          alt="未読"
                          className={styles.menu_unread_icon}
                        />
                      )}
                    </div>
                  </a>
                </Link>
              </div>

              {hasReferSellerService && (
                <div className={styles.sideMenuItem}>
                  <Link href={`/console/financial_agency/chat/referral_sellers`}>
                    <a>
                      <div
                        className={`${styles.menuText} ${
                          router.pathname === `/console/financial_agency/chat/referral_sellers` ||
                          router.pathname === `/console/financial_agency/chat/referral_sellers/[referral_seller_id]`
                            ? styles.current
                            : ''
                        }`}
                      >
                        紹介済みの売主様
                      </div>
                    </a>
                  </Link>
                </div>
              )}
              {hasReferSellerService && (
                <div className={styles.sideMenuItem}>
                  <Link href={`/console/financial_agency/chat/referral_sellers/create`}>
                    <a>
                      <div
                        className={`${styles.menuText} ${
                          router.pathname === `/console/financial_agency/chat/referral_sellers/create`
                            ? styles.current
                            : ''
                        }`}
                      >
                        売主様ご紹介フォーム
                      </div>
                    </a>
                  </Link>
                </div>
              )}
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};
export default FinancialAgencySideMenu;
